/** Data hooks for §8 Follow-ups. */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import * as followUpsApi from '../../api/followups.api.js';
import { queryKeys } from '../../app/queryClient.js';
import { pushToast } from '../ui/uiSlice.js';

/** @param {object} params e.g. `{ status, overdue, due_before }` — response carries `{data, counts}`. */
export function useFollowUps(params, enabled = true) {
  return useQuery({
    queryKey: queryKeys.followUps.list(params),
    queryFn: () => followUpsApi.listFollowUps(params),
    enabled,
  });
}

function invalidate(queryClient) {
  queryClient.invalidateQueries({ queryKey: queryKeys.followUps.all });
}

export function useCreateFollowUp() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  return useMutation({
    mutationFn: (payload) => followUpsApi.createFollowUp(payload),
    onSuccess: () => {
      invalidate(queryClient);
      dispatch(pushToast({ tone: 'success', message: 'Follow-up scheduled.' }));
    },
    onError: (error) => dispatch(pushToast({ tone: 'danger', message: 'Could not schedule that follow-up', detail: error.message })),
  });
}

export function useUpdateFollowUp() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  return useMutation({
    mutationFn: ({ id, patch, rowVersion }) => followUpsApi.updateFollowUp(id, patch, rowVersion),
    onSuccess: () => invalidate(queryClient),
    onError: (error) => {
      const stale = error?.code === 'stale_write';
      dispatch(pushToast({
        tone: 'danger',
        message: stale ? 'Somebody else changed this' : 'Could not save that change',
        detail: stale ? 'Refresh to pick up their version, then try again.' : error.message,
      }));
    },
  });
}

export function useCompleteFollowUp() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  return useMutation({
    mutationFn: (id) => followUpsApi.completeFollowUp(id),
    onSuccess: () => {
      invalidate(queryClient);
      dispatch(pushToast({ tone: 'success', message: 'Marked done.' }));
    },
    onError: (error) => {
      const already = error?.code === 'already_completed';
      dispatch(pushToast({
        tone: 'danger', message: already ? 'Already marked done' : 'Could not complete that',
        detail: already ? undefined : error.message,
      }));
    },
  });
}

export function useSnoozeFollowUp() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  return useMutation({
    mutationFn: ({ id, payload }) => followUpsApi.snoozeFollowUp(id, payload),
    onSuccess: () => {
      invalidate(queryClient);
      dispatch(pushToast({ tone: 'success', message: 'Follow-up re-planned.' }));
    },
    onError: (error) => dispatch(pushToast({ tone: 'danger', message: 'Could not snooze that follow-up', detail: error.message })),
  });
}
